/**
 * 安全工具函數庫
 * 提供各種安全相關的工具函數，防止常見的 Web 安全漏洞
 */

/**
 * HTML 轉義映射表
 */
const HTML_ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#039;',
  '/': '&#x2F;',
};

/**
 * HTML 轉義函數 - 防止 XSS 攻擊
 *
 * @param unsafe - 未經處理的字串（可能包含惡意腳本）
 * @returns 安全的 HTML 字串
 *
 * @example
 * ```typescript
 * const userInput = '<script>alert("XSS")</script>';
 * const safe = escapeHtml(userInput);
 * // safe = '&lt;script&gt;alert(&quot;XSS&quot;)&lt;/script&gt;'
 * ```
 */
export function escapeHtml(unsafe: string): string {
  if (typeof unsafe !== 'string') {
    return '';
  }
  return unsafe.replace(/[&<>"'/]/g, (char) => HTML_ESCAPE_MAP[char] || char);
}

/**
 * 安全地設定元素的文字內容
 * 使用 textContent 而非 innerHTML 來避免 XSS
 *
 * @param element - 目標 HTML 元素
 * @param text - 要設定的文字
 */
export function setTextContent(element: HTMLElement, text: string): void {
  element.textContent = text;
}

/**
 * 驗證 URL 是否安全
 * 防止 JavaScript 偽協議和其他不安全的 URL
 *
 * @param url - 要驗證的 URL
 * @returns 是否為安全的 URL
 */
export function isValidUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // 禁止 JavaScript 偽協議
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:'];
  const lowercaseUrl = url.toLowerCase().trim();

  if (dangerousProtocols.some(protocol => lowercaseUrl.startsWith(protocol))) {
    return false;
  }

  // 允許 http, https, mailto, tel 等安全協議
  const safeProtocolRegex = /^(https?|mailto|tel):/i;
  const relativeUrlRegex = /^[./]/;

  return safeProtocolRegex.test(url) || relativeUrlRegex.test(url);
}

/**
 * 驗證並清理使用者輸入
 *
 * @param input - 使用者輸入
 * @param maxLength - 最大長度限制
 * @returns 清理後的輸入
 */
export function sanitizeInput(input: string, maxLength: number = 1000): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  return input
    .trim()
    .substring(0, maxLength)
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ''); // 移除控制字元
}

/**
 * 生成隨機的 CSRF token
 *
 * @returns 隨機的 CSRF token
 */
export function generateCsrfToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * AES 加密（用於 localStorage）
 * 注意：前端加密只是模糊化，真正的安全性需要後端 httpOnly cookies
 *
 * @param data - 要加密的資料
 * @param key - 加密金鑰
 * @returns 加密後的字串
 */
export async function encrypt(data: string, key: string): Promise<string> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const keyBuffer = encoder.encode(key.padEnd(32, '0').substring(0, 32));

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBuffer,
    { name: 'AES-GCM' },
    false,
    ['encrypt']
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    dataBuffer
  );

  // 將 IV 和加密資料組合
  const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encryptedBuffer), iv.length);

  // 轉換為 Base64
  return btoa(String.fromCharCode(...combined));
}

/**
 * 檢查字串是否為有效的 Base64 格式
 *
 * @param str - 要檢查的字串
 * @returns 是否為有效的 Base64
 */
function isValidBase64(str: string): boolean {
  if (!str || typeof str !== 'string' || str.trim() === '') {
    return false;
  }
  // Base64 正規表達式（支援標準和 URL-safe 格式）
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
  return base64Regex.test(str) && str.length % 4 === 0;
}

/**
 * AES 解密（用於 localStorage）
 *
 * @param encryptedData - 加密的資料
 * @param key - 解密金鑰
 * @returns 解密後的字串
 */
export async function decrypt(encryptedData: string, key: string): Promise<string> {
  try {
    // 驗證輸入是否為有效的 Base64 字串
    if (!isValidBase64(encryptedData)) {
      throw new Error('無效的加密資料格式');
    }

    const decoder = new TextDecoder();
    const encoder = new TextEncoder();
    const keyBuffer = encoder.encode(key.padEnd(32, '0').substring(0, 32));

    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    // 從 Base64 解碼
    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));

    // 驗證資料長度（至少需要 12 bytes IV + 1 byte 資料）
    if (combined.length < 13) {
      throw new Error('加密資料長度不足');
    }

    // 分離 IV 和加密資料
    const iv = combined.slice(0, 12);
    const encryptedBuffer = combined.slice(12);

    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      encryptedBuffer
    );

    return decoder.decode(decryptedBuffer);
  } catch (error) {
    console.error('[Security] Decryption failed:', error);
    throw new Error('解密失敗');
  }
}

/**
 * 安全地解析 JSON
 *
 * @param jsonString - JSON 字串
 * @param fallback - 解析失敗時的預設值
 * @returns 解析後的物件或預設值
 */
export function safeJsonParse<T>(jsonString: string, fallback: T): T {
  try {
    return JSON.parse(jsonString) as T;
  } catch {
    return fallback;
  }
}

/**
 * 安全相關的常數
 */
export const SECURITY_CONSTANTS = {
  /**
   * 最大檔案大小 (10MB)
   */
  MAX_FILE_SIZE: 10 * 1024 * 1024,

  /**
   * 允許的圖片 MIME types
   */
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const,

  /**
   * 允許的圖片副檔名
   */
  ALLOWED_IMAGE_EXTENSIONS: ['jpg', 'jpeg', 'png', 'gif', 'webp'] as const,

  /**
   * Token 過期時間 (24 小時)
   */
  TOKEN_EXPIRY_MS: 24 * 60 * 60 * 1000,

  /**
   * CSRF Token 的 Header 名稱
   */
  CSRF_HEADER_NAME: 'X-CSRF-Token',

  /**
   * CSRF Token 的 localStorage key
   */
  CSRF_TOKEN_KEY: 'csrf_token',
} as const;
